const RAG_STATUS_FIELDS = ['otj_rag', 'workshop_rag', 'bil_rag', 'oof_rag'];

const OVERALL_RAG_PILL_TYPE_MAP = {
  Green: 'success',
  'Green - In Assessment': 'success',
  'First 42 days': 'neutral',
  Amber: 'warning',
  Red: 'danger',
};

const CUSTOM_OVERALL_RAG_LABELS = ['First 42 days', 'Green - In Assessment'];

export function isFirst42DayExclusion(ragData) {
  return Boolean(ragData?.first_42_day_exclusion);
}

export function getRagStatus(ragData, field) {
  if (!ragData) {
    return undefined;
  }
  return isFirst42DayExclusion(ragData) ? 'green' : ragData[field];
}

export function getOverallRagColour(ragData) {
  if (!ragData) {
    return null;
  }
  if (ragData.overall_rag) {
    return ragData.overall_rag;
  }
  const statuses = RAG_STATUS_FIELDS.map(field => ragData[field]);
  if (statuses.includes('red')) {
    return 'red';
  }
  if (statuses.includes('amber')) {
    return 'amber';
  }
  return 'green';
}

export function getOverallRagLabel(ragData) {
  if (!ragData) {
    return undefined;
  }
  if (isFirst42DayExclusion(ragData)) {
    return 'First 42 days';
  }
  if (ragData.is_in_epa) {
    return 'Green - In Assessment';
  }
  const colour = getOverallRagColour(ragData);
  return colour ? colour.charAt(0).toUpperCase() + colour.slice(1) : '-';
}

export function getOverallRagTableValue(ragData) {
  if (!ragData) {
    return undefined;
  }
  let overallRag = ragData.overall_rag;
  if (ragData.is_in_epa) {
    overallRag = 'Green - In Assessment';
  }
  if (isFirst42DayExclusion(ragData)) {
    overallRag = 'First 42 days';
  }
  return overallRag;
}

export function getOverallRagPillType(label) {
  return OVERALL_RAG_PILL_TYPE_MAP[label] || 'empty';
}

export function overallRagPillUsesCustomLabel(label) {
  return CUSTOM_OVERALL_RAG_LABELS.includes(label);
}

export function getOofMetricValue(ragData) {
  const days = ragData?.days_post_planned_end_date;
  if (days == null) {
    return 'No end date';
  }
  if (days > 0) {
    return `${days} days past end date`;
  }
  return 'Funded';
}

export function getPlannedEndDate(ragData) {
  if (ragData?.planned_end_date) {
    return ragData.planned_end_date;
  }
  const days = ragData?.days_post_planned_end_date;
  if (days == null) {
    return null;
  }
  const endDate = new Date();
  endDate.setHours(0, 0, 0, 0);
  endDate.setDate(endDate.getDate() - days);
  return endDate;
}

export function getFundingMetricDetail(ragData, formatDate) {
  const plannedEndDate = getPlannedEndDate(ragData);
  if (!plannedEndDate) {
    return 'No end date';
  }
  const formattedDate = formatDate(plannedEndDate);
  const days = ragData?.days_post_planned_end_date;
  if (days == null || days <= 0) {
    return `Funded until ${formattedDate}`;
  }
  return `Funding stopped ${formattedDate}`;
}

export function isOnABreak(ragData) {
  return Boolean(ragData?.expected_return_date);
}

export function getBilMetricDetail(ragData, formatDate) {
  if (!ragData?.expected_return_date) {
    return 'Not on a break';
  }
  return formatDate(ragData.expected_return_date);
}

export function getOtjHoursSummary(ragData, formatOtjHours) {
  if (!ragData) {
    return null;
  }
  if (ragData.approved_hours == null && ragData.expected_otj_hours_to_month == null) {
    return '0h / 0h';
  }
  return `${formatOtjHours(ragData.approved_hours ?? 0)} / ${formatOtjHours(ragData.expected_otj_hours_to_month)}`;
}

export function getWorkshopsSummary(ragData) {
  if (!ragData) {
    return null;
  }
  if (ragData.workshops_completed == null && ragData.total_workshops == null) {
    return '0 of 0 workshops';
  }
  return `${ragData.workshops_completed ?? '0'} of ${ragData.total_workshops ?? '0'} workshops`;
}

export function getAssignmentCompletionPercentage(ragData) {
  return Math.round((ragData?.paks_completed_percentage ?? 0) * 100) / 100;
}

export function getAssignmentsSummary(ragData) {
  if (!ragData) {
    return null;
  }
  if (ragData.paks_completed == null && ragData.total_paks == null) {
    return null;
  }
  return `${ragData.paks_completed ?? 0} of ${ragData.total_paks ?? '-'} assignments`;
}
